<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Eye, Pencil, Plus, Trash2 } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import PaginationControls from '../../components/PaginationControls.vue'
import {
  deleteProduct,
  listCatalogProducts,
  listProductAudiences,
  listProductSeasons,
} from '../productService'
import {
  productDetailPath,
} from '../productPresentation'
import type {
  CatalogKind,
  EngineeringProduct,
  ProductAudienceOption,
  ProductSeasonOption,
} from '../types'

const client = useApiClient()
const { t } = useI18n()
const auth = useAuthStore()
const route = useRoute()
const products = ref<EngineeringProduct[]>([])
const audiences = ref<ProductAudienceOption[]>([])
const seasons = ref<ProductSeasonOption[]>([])
const search = ref('')
const type = ref('')
const seasonId = ref('')
const audienceId = ref('')
const readiness = ref('')
const loading = ref(false)
const deleting = ref(false)
const deletingId = ref<number | null>(null)
const errorMessage = ref('')
const selectionMessage = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const selectedProductIds = ref<number[]>([])
let searchTimer: ReturnType<typeof setTimeout> | null = null
const catalogKind = computed<CatalogKind>(() =>
  route.meta.catalogKind === 'raw-materials' ||
  route.meta.catalogKind === 'spare-parts'
    ? route.meta.catalogKind
    : 'manufactured',
)
const isManufacturedCatalog = computed(
  () => catalogKind.value === 'manufactured',
)
const canCreateProducts = computed(
  () => auth.user?.permissions.includes('products.create') ?? false,
)
const canUpdateProducts = computed(
  () => auth.user?.permissions.includes('products.update') ?? false,
)
const canDeleteProducts = computed(
  () => auth.user?.permissions.includes('products.delete') ?? false,
)
const pageCopy = computed(() => {
  if (catalogKind.value === 'raw-materials') {
    return {
      eyebrow: t('engineering.catalog.rawEyebrow'),
      title: t('engineering.catalog.rawTitle'),
      description: t('engineering.catalog.rawDescription'),
      createPath: '/raw-materials/new',
    }
  }
  if (catalogKind.value === 'spare-parts') {
    return {
      eyebrow: t('engineering.catalog.spareEyebrow'),
      title: t('engineering.catalog.spareTitle'),
      description: t('engineering.catalog.spareDescription'),
      createPath: '/spare-parts/new',
    }
  }
  return {
    eyebrow: t('engineering.catalog.manufacturedEyebrow'),
    title: t('engineering.catalog.manufacturedTitle'),
    description: t('engineering.catalog.manufacturedDescription'),
    createPath: '/products/new',
  }
})
const sortedAudiences = computed(() =>
  [...audiences.value].sort((left, right) => left.name.localeCompare(right.name)),
)
const sortedSeasons = computed(() =>
  [...seasons.value].sort((left, right) => left.name.localeCompare(right.name)),
)

const visibleProducts = computed(() => {
  let result = products.value
  if (readiness.value) {
    const isReady = readiness.value === 'ready'
    result = result.filter(
      (product) => Boolean(product.production_readiness?.is_ready) === isReady,
    )
  }
  return result
})

const readyCount = computed(
  () => products.value.filter((product) => product.production_readiness?.is_ready).length,
)
const totalPages = computed(() =>
  Math.max(1, Math.ceil(visibleProducts.value.length / pageSize.value)),
)
const paginatedProducts = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return visibleProducts.value.slice(start, start + pageSize.value)
})
const selectedCount = computed(() => selectedProductIds.value.length)
const allVisibleSelected = computed(() =>
  Boolean(paginatedProducts.value.length)
  && paginatedProducts.value.every((product) => selectedProductIds.value.includes(product.id)),
)
const editActionLabel = computed(() =>
  t(`engineering.productForm.editAction.${catalogKind.value}`),
)

function productEditPath(product: Pick<EngineeringProduct, 'id' | 'type'>): string {
  return `${productDetailPath(product)}/edit`
}

async function loadProducts(): Promise<void> {
  loading.value = true
  errorMessage.value = ''
  selectionMessage.value = ''
  try {
    if (isManufacturedCatalog.value && (!audiences.value.length || !seasons.value.length)) {
      const [audienceList, seasonList] = await Promise.all([
        listProductAudiences(client),
        listProductSeasons(client),
      ])
      audiences.value = audienceList
      seasons.value = seasonList
    }
    products.value = await listCatalogProducts(client, catalogKind.value, {
      search: search.value.trim() || undefined,
      type: type.value || undefined,
      audience_id: isManufacturedCatalog.value && audienceId.value ? Number(audienceId.value) : undefined,
      season_id: isManufacturedCatalog.value && seasonId.value ? Number(seasonId.value) : undefined,
    })
  } catch (error: unknown) {
    products.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

function syncSelectedProducts(): void {
  const visibleIds = new Set(visibleProducts.value.map((product) => product.id))
  selectedProductIds.value = selectedProductIds.value.filter((id) => visibleIds.has(id))
}

function scheduleSearch(): void {
  currentPage.value = 1
  if (searchTimer) {
    clearTimeout(searchTimer)
  }
  searchTimer = setTimeout(loadProducts, 300)
}

function applyFilters(): void {
  currentPage.value = 1
  if (searchTimer) {
    clearTimeout(searchTimer)
    searchTimer = null
  }
  void loadProducts()
}

function toggleProductSelection(productId: number, checked: boolean): void {
  if (checked) {
    if (!selectedProductIds.value.includes(productId)) {
      selectedProductIds.value = [...selectedProductIds.value, productId]
    }
    return
  }
  selectedProductIds.value = selectedProductIds.value.filter((id) => id !== productId)
}

function togglePageSelection(checked: boolean): void {
  const pageIds = paginatedProducts.value.map((product) => product.id)
  if (checked) {
    selectedProductIds.value = Array.from(new Set([...selectedProductIds.value, ...pageIds]))
    return
  }
  selectedProductIds.value = selectedProductIds.value.filter((id) => !pageIds.includes(id))
}

async function deleteSelectedProducts(): Promise<void> {
  if (!selectedCount.value || deleting.value || !canDeleteProducts.value) return
  if (!window.confirm(t('engineering.catalog.confirmDeleteSelected', { count: selectedCount.value }))) return
  deleting.value = true
  errorMessage.value = ''
  selectionMessage.value = ''
  try {
    const deletedCount = selectedCount.value
    await Promise.all(selectedProductIds.value.map((productId) => deleteProduct(client, productId)))
    selectedProductIds.value = []
    selectionMessage.value = t('engineering.catalog.deletedSelected', { count: deletedCount })
    await loadProducts()
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    deleting.value = false
  }
}

async function deleteSingleProduct(product: EngineeringProduct): Promise<void> {
  if (!canDeleteProducts.value || deletingId.value != null) return
  if (!window.confirm(t('engineering.catalog.confirmDeleteSingle', { name: product.name }))) return
  deletingId.value = product.id
  errorMessage.value = ''
  selectionMessage.value = ''
  try {
    await deleteProduct(client, product.id)
    selectedProductIds.value = selectedProductIds.value.filter((id) => id !== product.id)
    selectionMessage.value = t('engineering.catalog.deletedSingle', { name: product.name })
    await loadProducts()
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    deletingId.value = null
  }
}

onMounted(loadProducts)
watch(search, scheduleSearch)
watch([type, seasonId, audienceId, readiness], () => {
  currentPage.value = 1
})
watch([visibleProducts, pageSize], () => {
  currentPage.value = Math.min(currentPage.value, totalPages.value)
  syncSelectedProducts()
})
watch(catalogKind, () => {
  search.value = ''
  type.value = ''
  seasonId.value = ''
  audienceId.value = ''
  readiness.value = ''
  currentPage.value = 1
  selectedProductIds.value = []
  void loadProducts()
})
onBeforeUnmount(() => {
  if (searchTimer) {
    clearTimeout(searchTimer)
  }
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ pageCopy.eyebrow }}</p>
      <h2>{{ pageCopy.title }}</h2>
      <p>{{ pageCopy.description }}</p>
    </div>
    <div class="heading-actions">
      <div v-if="isManufacturedCatalog" class="summary">
        <strong>{{ readyCount }}</strong>
        <span>{{ t('engineering.catalog.readyOf', { total: products.length }) }}</span>
      </div>
      <RouterLink
        v-if="canCreateProducts"
        class="create-link"
        :to="pageCopy.createPath"
      >
        <Plus :size="16" aria-hidden="true" />
        {{ $t(`engineering.productForm.createAction.${catalogKind}`) }}
      </RouterLink>
      <span
        v-else
        class="create-link create-link--disabled"
        :title="$t('engineering.productForm.permissionHint')"
        :aria-label="$t('engineering.productForm.permissionHint')"
      >
        <Plus :size="16" aria-hidden="true" />
        {{ $t(`engineering.productForm.createAction.${catalogKind}`) }}
      </span>
    </div>
  </div>

  <form
    class="filters"
    :class="{ 'filters--compact': !isManufacturedCatalog }"
    @submit.prevent="applyFilters"
  >
    <label>
      <span>{{ t('engineering.catalog.search') }}</span>
      <input
        v-model="search"
        type="search"
        :placeholder="t('engineering.catalog.searchPlaceholder')"
      >
    </label>
    <label v-if="isManufacturedCatalog">
      <span>{{ t('engineering.catalog.type') }}</span>
      <select v-model="type">
        <option value="">{{ t('engineering.catalog.allManufacturedTypes') }}</option>
        <option value="manufactured_good">{{ t('engineering.catalog.manufacturedGood') }}</option>
        <option value="finished_good">{{ t('engineering.catalog.finishedGood') }}</option>
        <option value="semi_finished_good">{{ t('engineering.catalog.semiFinishedGood') }}</option>
        <option value="semi_finished">{{ t('engineering.catalog.semiFinished') }}</option>
      </select>
    </label>
    <label v-if="isManufacturedCatalog">
      <span>{{ t('engineering.catalog.season') }}</span>
      <select v-model="seasonId">
        <option value="">{{ t('engineering.catalog.allSeasons') }}</option>
        <option v-for="seasonOption in sortedSeasons" :key="seasonOption.id" :value="String(seasonOption.id)">
          {{ seasonOption.name }}
        </option>
      </select>
    </label>
    <label v-if="isManufacturedCatalog">
      <span>{{ t('engineering.catalog.audience') }}</span>
      <select v-model="audienceId">
        <option value="">{{ t('engineering.catalog.allAudiences') }}</option>
        <option v-for="audience in sortedAudiences" :key="audience.id" :value="String(audience.id)">
          {{ audience.name }}
        </option>
      </select>
    </label>
    <label v-if="isManufacturedCatalog">
      <span>{{ t('engineering.catalog.readiness') }}</span>
      <select v-model="readiness">
        <option value="">{{ t('engineering.catalog.allStatuses') }}</option>
        <option value="ready">{{ t('engineering.catalog.ready') }}</option>
        <option value="not-ready">{{ t('engineering.catalog.notReady') }}</option>
      </select>
    </label>
    <button type="submit" :disabled="loading">{{ t('engineering.catalog.apply') }}</button>
  </form>

  <div v-if="selectionMessage" class="message message--success" role="status">
    <span>{{ selectionMessage }}</span>
  </div>

  <div v-if="errorMessage" class="message message--error" role="alert">
    <span>{{ errorMessage }}</span>
    <button type="button" @click="loadProducts">{{ t('engineering.action.retry') }}</button>
  </div>

  <div class="table-shell" :aria-busy="loading">
    <table>
      <thead>
        <tr>
          <th v-if="canDeleteProducts" class="select-column">
            <input
              :checked="allVisibleSelected"
              type="checkbox"
              :aria-label="t('engineering.catalog.selectPageProducts')"
              @change="togglePageSelection(($event.target as HTMLInputElement).checked)"
            >
          </th>
          <th>{{ t('engineering.catalog.product') }}</th>
          <th>{{ t('engineering.catalog.classification') }}</th>
          <th>{{ t('engineering.catalog.unit') }}</th>
          <th v-if="isManufacturedCatalog">{{ t('engineering.catalog.readiness') }}</th>
          <th><span class="visually-hidden">{{ t('engineering.catalog.actions') }}</span></th>
        </tr>
      </thead>
      <tbody v-if="!loading && visibleProducts.length">
        <tr v-for="product in paginatedProducts" :key="product.id">
          <td v-if="canDeleteProducts" class="select-column">
            <input
              :checked="selectedProductIds.includes(product.id)"
              type="checkbox"
              :aria-label="t('engineering.catalog.selectProduct', { name: product.name })"
              @change="toggleProductSelection(product.id, ($event.target as HTMLInputElement).checked)"
            >
          </td>
          <td>
            <strong>
              <RouterLink class="product-link" :to="productDetailPath(product)">{{ product.name }}</RouterLink>
            </strong>
            <span>{{ product.sku }}</span>
          </td>
          <td>
            {{ product.group?.name || product.type }}
            <span>{{ product.season_info?.name || product.season || t('engineering.catalog.noSeason') }}</span>
          </td>
          <td>{{ product.unit }}</td>
          <td v-if="isManufacturedCatalog">
            <span
              class="status"
              :class="product.production_readiness?.is_ready
                ? 'status--ready'
                : 'status--blocked'"
            >
              {{ product.production_readiness?.is_ready ? t('engineering.catalog.ready') : t('engineering.catalog.notReady') }}
            </span>
            <small v-if="!product.production_readiness?.is_ready">
              {{ t('engineering.catalog.gaps', { count: product.production_readiness?.missing_requirements.length ?? 0 }) }}
            </small>
          </td>
          <td class="row-action">
            <div class="row-actions">
              <RouterLink class="icon-link" :to="productDetailPath(product)">
                <Eye :size="16" aria-hidden="true" />
                {{ t('engineering.catalog.review') }}
              </RouterLink>
              <RouterLink
                v-if="canUpdateProducts"
                class="icon-link"
                :to="productEditPath(product)"
              >
                <Pencil :size="16" aria-hidden="true" />
                {{ editActionLabel }}
              </RouterLink>
              <button
                v-if="canDeleteProducts"
                type="button"
                class="icon-button icon-button--danger"
                :disabled="deletingId === product.id"
                @click="deleteSingleProduct(product)"
              >
                <Trash2 :size="16" aria-hidden="true" />
                {{ t('engineering.catalog.deleteAction') }}
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="loading" class="table-state">{{ t('engineering.catalog.loadingProducts') }}</div>
    <div
      v-else-if="!visibleProducts.length && !errorMessage"
      class="table-state"
    >
      {{ t('engineering.catalog.emptyProducts') }}
    </div>
    <PaginationControls
      v-if="!loading && visibleProducts.length"
      v-model:page="currentPage"
      v-model:page-size="pageSize"
      :total="visibleProducts.length"
      :page-size-options="[10, 25, 50]"
    />
  </div>

  <div
    v-if="canDeleteProducts"
    class="selection-bar"
    :class="{ 'selection-bar--active': selectedCount > 0 }"
  >
    <span>{{ t('engineering.catalog.selectedProducts', { count: selectedCount }) }}</span>
    <button
      type="button"
      class="selection-delete"
      :disabled="!selectedCount || deleting"
      @click="deleteSelectedProducts"
    >
      {{ deleting ? t('engineering.catalog.deletingSelected') : t('engineering.catalog.deleteSelected') }}
    </button>
  </div>
</template>

<style scoped>
.page-heading {
  display: flex;
  gap: 24px;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 24px;
}

.heading-actions {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.eyebrow {
  margin: 0 0 5px;
  color: #66726e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 26px;
  letter-spacing: 0;
}

.page-heading p:last-child {
  margin: 7px 0 0;
  color: #66726e;
}

.summary {
  display: grid;
  min-width: 112px;
  padding-left: 18px;
  border-left: 3px solid #3568a8;
}

.summary strong {
  font-size: 24px;
}

.summary span {
  color: #66726e;
  font-size: 12px;
}

.create-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid #3568a8;
  border-radius: 8px;
  color: #ffffff;
  background: #3568a8;
  font-weight: 700;
  text-decoration: none;
}

.create-link--disabled {
  border-color: #b9c5c1;
  color: #6f7d78;
  background: #eef2f1;
  cursor: not-allowed;
}

.filters {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) repeat(5, minmax(120px, 150px)) auto;
  gap: 12px;
  align-items: end;
  padding: 16px;
  border: 1px solid #d7dfdc;
  background: #ffffff;
}

.filters--compact {
  grid-template-columns: minmax(240px, 1fr) auto;
}

label span {
  display: block;
  margin-bottom: 6px;
  color: #4e5d57;
  font-size: 12px;
  font-weight: 700;
}

input,
select,
button {
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #adb9b4;
  border-radius: 8px;
  color: #17201d;
  background: #ffffff;
}

button {
  width: auto;
  cursor: pointer;
}

.filters > button {
  border-color: #3568a8;
  color: #ffffff;
  background: #3568a8;
}

.selection-bar {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid #d7dfdc;
  background: #f8faf9;
}

.selection-bar--active {
  border-color: #d7a8a1;
  background: #fff7f5;
}

.selection-delete {
  border-color: #9b3025;
  color: #ffffff;
  background: #9b3025;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.message {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid #d7a8a1;
  color: #852d22;
  background: #fff5f3;
}

.message--success {
  border-color: #9ac7b5;
  color: #236451;
  background: #eef8f3;
}

.table-shell {
  min-height: 240px;
  margin-top: 16px;
  overflow-x: auto;
  border: 1px solid #d7dfdc;
  background: #ffffff;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

th,
td {
  padding: 13px 14px;
  border-bottom: 1px solid #e1e6e4;
  text-align: left;
  vertical-align: middle;
}

th {
  color: #586660;
  background: #f3f6f5;
  font-size: 12px;
  text-transform: uppercase;
}

.select-column {
  width: 1%;
  white-space: nowrap;
}

.select-column input {
  width: 16px;
  min-height: 16px;
  padding: 0;
}

td strong,
td > span:not(.status),
td small {
  display: block;
}

td > span:not(.status),
td small {
  margin-top: 3px;
  color: #71807a;
  font-size: 12px;
}

.status {
  display: inline-block;
  width: max-content;
  padding: 4px 7px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.status--ready {
  border-color: #86b4a4;
  color: #236451;
  background: #eff8f5;
}

.status--blocked {
  border-color: #d2b37c;
  color: #765017;
  background: #fff8e9;
}

.row-action {
  width: 1%;
  white-space: nowrap;
}

.product-link,
.row-action a {
  color: #285b9a;
  font-weight: 700;
}

.row-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.icon-link,
.icon-button {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  width: auto;
  min-height: 34px;
  padding: 6px 10px;
  border: 1px solid #b9c5c1;
  color: #285b9a;
  background: #ffffff;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}

.icon-button--danger {
  color: #8b3025;
}

.table-state {
  display: grid;
  min-height: 180px;
  color: #697671;
  place-items: center;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

@media (max-width: 900px) {
  .filters {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .filters--compact {
    grid-template-columns: minmax(240px, 1fr) auto;
  }
}

@media (max-width: 620px) {
  .page-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .heading-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .row-actions,
  .selection-bar,
  .filters {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
